import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Send, Truck, Package as PackageIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  customer_name: "", customer_email: "", customer_phone: "", organization: "",
  event_date: "", event_time: "", guests: "", fulfillment: "delivery", zip_code: "", notes: "",
};

const Field = ({ id, label, required, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/70">
      {label}{required && <span className="text-brand-gold-dark"> *</span>}
    </Label>
    {children}
  </div>
);

const FulfillmentToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" role="radiogroup">
    {[["delivery", "Delivery", Truck], ["pickup", "Pickup", PackageIcon]].map(([key, label, Icon]) => (
      <button
        key={key}
        type="button"
        role="radio"
        aria-checked={value === key}
        onClick={() => onChange(key)}
        data-testid={`item-quote-fulfillment-${key}`}
        className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
          value === key ? "bg-brand-charcoal text-white" : "bg-white border border-black/10 text-brand-charcoal/70 hover:bg-brand-cream"
        }`}
      >
        <Icon className="h-3.5 w-3.5" /> {label}
      </button>
    ))}
  </div>
);

export const ItemQuoteModal = ({ item, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!item) return;
    setForm({
      ...EMPTY,
      customer_name: user?.name || "",
      customer_email: user?.email || "",
      customer_phone: user?.phone || "",
      organization: user?.organization || "",
    });
  }, [item, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (form.fulfillment === "delivery" && !/^\d{5}$/.test(form.zip_code.trim())) {
      toast.error("Please enter a 5-digit delivery ZIP code.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quote/item", {
        ...form,
        item_id: item.id,
        guests: Number(form.guests),
        zip_code: form.fulfillment === "delivery" ? form.zip_code.trim() : null,
        organization: form.organization || null,
        event_time: form.event_time || null,
        notes: form.notes || null,
      });
      onClose();
      navigate(`/checkout/quote-received?quote_id=${data.id}&reason=item_quote`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your request. Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="item-quote-modal" className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-teal">Request a quote</p>
          <DialogTitle data-testid="item-quote-item-name" className="font-display font-black text-2xl tracking-tight text-brand-charcoal">
            {item?.name}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-brand-charcoal/65">
            Pricing for this item is tailored to your event. Tell us a bit about it and we&apos;ll email a custom quote within 24 hours.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="item-quote-form">
          <Field id="iq-name" label="Full name" required>
            <Input id="iq-name" required value={form.customer_name} onChange={set("customer_name")} data-testid="item-quote-name" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field id="iq-email" label="Email" required>
              <Input id="iq-email" type="email" required value={form.customer_email} onChange={set("customer_email")} data-testid="item-quote-email" />
            </Field>
            <Field id="iq-phone" label="Phone" required>
              <Input id="iq-phone" type="tel" required value={form.customer_phone} onChange={set("customer_phone")} data-testid="item-quote-phone" />
            </Field>
          </div>
          <Field id="iq-org" label="Company or agency">
            <Input id="iq-org" value={form.organization} onChange={set("organization")} data-testid="item-quote-organization" />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field id="iq-date" label="Event date" required>
              <Input id="iq-date" type="date" min={today} required value={form.event_date} onChange={set("event_date")} data-testid="item-quote-event-date" />
            </Field>
            <Field id="iq-time" label="Time">
              <Input id="iq-time" type="time" value={form.event_time} onChange={set("event_time")} data-testid="item-quote-event-time" />
            </Field>
            <Field id="iq-guests" label="Guests" required>
              <Input id="iq-guests" type="number" min={1} max={10000} required value={form.guests} onChange={set("guests")} data-testid="item-quote-guests" />
            </Field>
          </div>
          <Field id="iq-fulfillment" label="Delivery or pickup" required>
            <FulfillmentToggle value={form.fulfillment} onChange={set("fulfillment")} />
          </Field>
          {form.fulfillment === "delivery" && (
            <Field id="iq-zip" label="Delivery ZIP" required>
              <Input id="iq-zip" inputMode="numeric" maxLength={5} required value={form.zip_code} onChange={set("zip_code")} data-testid="item-quote-zip" />
            </Field>
          )}
          <Field id="iq-notes" label="Notes or special requests">
            <Textarea id="iq-notes" rows={3} maxLength={1000} value={form.notes} onChange={set("notes")} data-testid="item-quote-notes" />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            data-testid="item-quote-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submitting ? "Sending…" : "Send quote request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ItemQuoteModal;
